import { useEffect, useRef } from 'react'
import Styles from './Screenshot.module.scss'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { useAsyncScreenshot } from 'hooks/queries/useAsyncScreenshot'

const SCREENSHOT_TIMEOUT = 60000

interface Props {
  url?: string
  handleBack: () => void
  cancelOnError?: boolean
}

export const Screenshot = ({ url, handleBack, cancelOnError = true }: Props) => {
  const { screenshotUrl, isLoading, createScreenshot } = useAsyncScreenshot({
    onError: () => {
      if (cancelOnError) handleBack()
    },
  })

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasStartedRef = useRef(false)

  useEffect(() => {
    if (screenshotUrl || isLoading || hasStartedRef.current || !url) {
      return
    }

    hasStartedRef.current = true

    timeoutRef.current = setTimeout(() => {
      if (cancelOnError) handleBack()
    }, SCREENSHOT_TIMEOUT)

    createScreenshot(url)
      .catch(() => {
        // Errors are surfaced through onError; swallow to avoid unhandled rejections in the UI layer.
      })
      .finally(() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
      })

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [createScreenshot, screenshotUrl, isLoading, url, handleBack, cancelOnError])

  return (
    <div className={Styles.imageContainer}>
      {screenshotUrl ? (
        <img src={screenshotUrl} alt="Initial Screenshot" />
      ) : (
        <LoadingShimmerer>
          <div className={Styles.screenshotPlaceholder} />
        </LoadingShimmerer>
      )}
    </div>
  )
}
